/**
 * Logo component — co-branded.
 *
 * - If a school brand is provided (via BrandingContext), shows:
 *     [School Logo] {School Name}
 *     Powered by MangoCubs
 *
 * - If no school brand, shows the default MangoCubs logo + wordmark.
 *
 * Props:
 *   size      — logo image size (px)
 *   wordmark  — show the brand name beside the logo (default true)
 *   tagline   — show the small tagline ("Powered by MangoCubs" or "Smart Solutions…")
 *   forceMangoCubs — ignore school brand, always render MangoCubs (for footers etc.)
 */
import { useBranding } from "@/contexts/BrandingContext";

export function Logo({ size = 36, wordmark = true, tagline = false, forceMangoCubs = false, className = "" }) {
  const { brand, isCoBranded } = useBranding();
  const effective = forceMangoCubs ? null : brand;
  const name = effective?.display_name || "MangoCubs";
  const logoUrl = effective?.logo_url || "/mangocubs-logo.png?v=20260514c";
  const showCoBrand = !forceMangoCubs && isCoBranded;
  const showWordmark = wordmark;

  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`} data-testid="brand-logo">
      <img
        src={logoUrl}
        alt={name}
        width={size}
        height={size}
        style={{ width: size, height: size, objectFit: "contain" }}
        className="shrink-0"
        data-testid="brand-logo-img"
      />
      {showWordmark && (
        <span className="leading-tight">
          <span
            className="font-heading font-extrabold text-xl tracking-tight block"
            style={{ color: showCoBrand ? "var(--brand-primary, #F58A1F)" : undefined }}
            data-testid="brand-name"
          >
            {showCoBrand ? (
              name
            ) : (
              <>
                Mango<span className="text-leaf">Cubs</span>
                <sup className="text-[10px] font-semibold ml-0.5 text-ink-soft">™</sup>
              </>
            )}
          </span>
          {(tagline || showCoBrand) && (
            <span
              className="text-[10px] font-medium tracking-wider text-mustard-hover block mt-0.5"
              data-testid="brand-tagline"
            >
              {showCoBrand ? "Powered by MangoCubs" : (effective?.tagline || "Smart Solutions for Early Learning")}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

/** Tiny inline footer mark — always shows "Powered by MangoCubs" for co-branded views. */
export function PoweredByMangoCubs({ className = "" }) {
  const { isCoBranded } = useBranding();
  if (!isCoBranded) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] text-ink-soft font-medium ${className}`}
      data-testid="powered-by-mangocubs"
    >
      Powered by
      <span className="font-heading font-bold text-mango-deep">
        Mango<span className="text-leaf">Cubs</span>
      </span>
    </span>
  );
}
